import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckSquare, CloudSun, Droplets, Eye, MapPin, RefreshCw, ShieldAlert,
         Snowflake, Square, Wind } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const LEVELS = {
  warning: { label: "Warning", cls: "bg-red-600 text-white border-red-700" },
  watch: { label: "Watch", cls: "bg-amber-500 text-royal-deep border-amber-600" },
  advisory: { label: "Advisory", cls: "bg-yellow-200 text-royal-deep border-yellow-400" },
  monitor: { label: "Monitor", cls: "bg-royal/10 text-royal border-royal/25" },
  normal: { label: "Normal", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

const FRESHNESS = {
  current: { label: "Current", cls: "text-emerald-700" },
  degraded: { label: "Partially unavailable", cls: "text-amber-700" },
  stale: { label: "Stale", cls: "text-amber-700" },
  unavailable: { label: "Unavailable", cls: "text-destructive" },
  updating: { label: "Updating", cls: "text-muted-foreground" },
  disabled: { label: "Switched off", cls: "text-muted-foreground" },
};

const TROPICAL = {
  direct_threat: "Cornerstone Ops analysis: direct or near-direct threat",
  increasing_concern: "Cornerstone Ops analysis: increasing concern",
  potential_impact: "Cornerstone Ops analysis: potential impact",
  monitoring: "Cornerstone Ops analysis: monitoring",
  warning: "Official tropical warning in effect",
  watch: "Official tropical watch in effect",
  advisory: "Official tropical advisory in effect",
};

const ago = (iso) => {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs} hr ago` : `${Math.round(hrs / 24)} days ago`;
};

/** Weather for ONE property, read from the platform's normalized record — never from the browser
 *  calling NWS itself, and never another property's data (the request is keyed on this id). */
export function WeatherPanel({ propertyId }) {
  const [wx, setWx] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBooks = useCallback(() => {
    if (!propertyId) return;
    api.get("/weather/playbooks", { params: { property_id: propertyId } })
      .then(({ data }) => setBooks(data.filter((b) => b.property_id === propertyId)))
      .catch(() => setBooks([]));
  }, [propertyId]);

  const load = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get(`/weather/property/${propertyId}`)
      .then(({ data }) => setWx(data.property_id === propertyId ? data : null))
      .catch(() => setWx(null))
      .finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => {
    setWx(null);
    setBooks([]);
    load();
    loadBooks();
    const t = setInterval(() => { load(); loadBooks(); }, 300000);
    return () => clearInterval(t);
  }, [load, loadBooks]);

  if (loading && !wx) {
    return <div data-testid="weather-loading" className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">Loading official weather…</div>;
  }
  if (!wx) return null;

  const fresh = FRESHNESS[wx.status] || FRESHNESS.unavailable;
  const level = LEVELS[wx.severity || "normal"] || LEVELS.normal;
  const cur = wx.weather?.current || {};
  const today = wx.weather?.today || {};
  const alerts = wx.alerts || [];
  const trop = wx.tropical || {};

  return (
    <div data-testid={`weather-panel-${propertyId}`} data-weather-status={wx.status}
         className="rounded-xl border border-border bg-card overflow-hidden min-w-0">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <CloudSun className="h-4 w-4 text-royal" />
          <h3 className="font-display text-base font-semibold text-royal-deep">Weather Intelligence</h3>
          <span data-testid="weather-severity" className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${level.cls}`}>
            {level.label}
          </span>
        </div>
        <button type="button" onClick={load} data-testid="weather-refresh-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-royal hover:text-gold transition-colors duration-200">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Reload
        </button>
      </div>

      {wx.status === "unavailable" || wx.status === "updating" || wx.status === "disabled" ? (
        <div className="px-5 py-5">
          <p data-testid="weather-unavailable" className="text-sm text-muted-foreground">
            {wx.message || wx.reason || "Official weather data is temporarily unavailable for this property."}
            {wx.last_successful_update && <> Last successful official update {ago(wx.last_successful_update)}.</>}
          </p>
          {wx.status === "unavailable" && <LocationFixer propertyId={propertyId} onFixed={load} />}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {books.map((b) => (
            <Playbook key={b._id} book={b} onChange={loadBooks} />
          ))}
          {(wx.freeze?.at_risk) && (
            <p data-testid="weather-freeze" className="flex items-start gap-2 px-4 py-3 text-xs text-sky-900 bg-sky-50 sm:px-5">
              <Snowflake className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Freeze risk: forecast low {wx.freeze.lowest_f}°F{wx.freeze.period ? ` (${wx.freeze.period})` : ""}.
                {wx.freeze.official_events?.length
                  ? ` Official ${wx.freeze.official_events.join(", ")} in effect (NWS).`
                  : " Cornerstone Ops analysis of the official NWS forecast."}
              </span>
            </p>
          )}
          {alerts.length > 0 && (
            <div className="space-y-2 px-4 py-4 sm:px-5" data-testid="weather-alerts">
              {alerts.map((a) => (
                <div key={a.alert_id} data-testid={`weather-alert-${a.level}`}
                     className={`rounded-lg border px-3.5 py-2.5 ${LEVELS[a.level]?.cls || LEVELS.monitor.cls}`}>
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <ShieldAlert className="h-4 w-4 shrink-0" /> {a.event}
                  </p>
                  <p className="mt-1 text-xs opacity-90">{a.headline}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-wider opacity-75">
                    National Weather Service{a.expires ? ` · until ${new Date(a.expires).toLocaleString()}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
          {wx.alerts_status && wx.alerts_status !== "current" && (
            <p data-testid="weather-alerts-unavailable" className="flex items-center gap-2 px-5 py-3 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Alert data could not be retrieved just now — this is not the same as "no alerts".
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-4 sm:gap-4 sm:px-5">
            <Stat label="Now" value={cur.temperature_f != null ? `${cur.temperature_f}°F` : "—"}
                  hint={cur.conditions} testid="weather-temp" />
            <Stat label="Feels like" value={cur.feels_like_f != null ? `${cur.feels_like_f}°F` : "—"} />
            <Stat label="Wind" value={cur.wind_mph != null ? `${cur.wind_mph} mph` : today.wind || "—"}
                  hint={cur.wind_gust_mph ? `gusts ${cur.wind_gust_mph} mph` : null} icon={Wind} />
            <Stat label="Rain chance" value={today.precip_probability_pct != null ? `${today.precip_probability_pct}%` : "—"}
                  hint={cur.humidity_pct != null ? `${cur.humidity_pct}% humidity` : null} icon={Droplets} />
          </div>

          {today.detail && (
            <div className="px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {today.name} · National Weather Service
              </p>
              <p className="mt-1.5 text-sm text-foreground leading-relaxed">{today.detail}</p>
            </div>
          )}

          {(wx.weather?.periods || []).length > 1 && (
            <div className="flex gap-3 overflow-x-auto px-5 py-4" data-testid="weather-forecast">
              {wx.weather.periods.slice(1, 6).map((p) => (
                <div key={p.name} className="min-w-[104px] rounded-lg bg-secondary px-3 py-2.5">
                  <p className="text-[11px] font-semibold text-royal-deep">{p.name}</p>
                  <p className="mt-1 text-lg font-display font-bold text-royal-deep">{p.temperature_f}°</p>
                  <p className="text-[11px] text-muted-foreground leading-snug">{p.short}</p>
                </div>
              ))}
            </div>
          )}

          <div className="px-4 py-4 sm:px-5" data-testid="weather-tropical">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tropical activity · National Hurricane Center
            </p>
            {wx.tropical_status !== "current" ? (
              <p className="mt-1.5 text-sm text-amber-700">NHC data temporarily unavailable.</p>
            ) : trop.storms?.length ? (
              <div className="mt-2 space-y-2">
                {trop.storms.map((s) => (
                  <div key={s.storm_id} className="rounded-lg border border-royal/20 bg-royal/5 px-3.5 py-2.5">
                    <p className="text-sm font-semibold text-royal-deep">
                      {s.classification} {s.name} · {s.distance_miles} miles away
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.intensity_kt} kt, moving {s.movement} at {s.movement_kt} kt
                      {s.advisory_number ? ` · advisory ${s.advisory_number}` : ""}
                    </p>
                    <p className="mt-1 text-[11px] font-medium text-royal">{TROPICAL[s.analysis_band]}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-muted-foreground">
                {trop.active_storms_tracked
                  ? `${trop.active_storms_tracked} active system(s) in the basin, none within 500 miles of this property.`
                  : "No active tropical cyclones."}
              </p>
            )}
          </div>

          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-[11px] text-muted-foreground sm:px-5">
            <span data-testid="weather-freshness" className={`font-semibold uppercase tracking-wider ${fresh.cls}`}>
              {fresh.label}
            </span>
            · Official data as of {ago(wx.last_successful_update)}
            {wx.nws_office && <> · NWS {wx.nws_office}</>}
            {wx.time_zone && <> · {wx.time_zone}</>}
            {cur.visibility_mi != null && <> · <Eye className="inline h-3 w-3" /> {cur.visibility_mi} mi</>}
          </p>
        </div>
      )}
    </div>
  );
}

/** Storm or freeze checklist raised automatically for this property. */
function Playbook({ book, onChange }) {
  const [items, setItems] = useState(book.items);
  const [busy, setBusy] = useState(null);
  const done = items.filter((i) => i.done).length;

  const tick = async (key, next) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/weather/playbooks/${book._id}/items/${key}`, { done: next });
      setItems(data.items);
      onChange?.();
    } catch {
      toast.error("Could not save that step — try again");
    }
    setBusy(null);
  };

  return (
    <div data-testid={`playbook-${book._id}`} className="px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-royal-deep">
          {book.kind === "freeze" ? <Snowflake className="h-4 w-4 text-sky-600" />
            : <ShieldAlert className="h-4 w-4 text-red-600" />}
          {book.title}
        </p>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {done}/{items.length} done
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Triggered by {book.event} · Cornerstone Ops checklist
      </p>
      <ul className="mt-2.5 space-y-1.5">
        {items.map((i) => (
          <li key={i.key}>
            <button type="button" disabled={busy === i.key}
                    data-testid={`playbook-step-${i.key}`}
                    onClick={() => tick(i.key, !i.done)}
                    className="flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors duration-200 hover:bg-secondary">
              {i.done ? <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                : <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className={`text-sm ${i.done ? "text-muted-foreground line-through" : "text-foreground"}`}>
                {i.label}
                {i.done && i.done_by && (
                  <span className="ml-1.5 text-[11px] text-muted-foreground no-underline">
                    · {i.done_by}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Correct a property whose address no map service can find. */
function LocationFixer({ propertyId, onFixed }) {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/weather/property/${propertyId}/location`, payload);
      toast.success(data.status === "unavailable"
        ? "Location saved, but the forecast is still resolving"
        : `Location fixed — NWS ${data.nws_office || ""} ${data.time_zone || ""}`);
      setOpen(false);
      onFixed?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save that location");
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <button type="button" data-testid="fix-location-btn" onClick={() => setOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-royal/30 px-3 py-2 text-xs font-semibold text-royal transition-colors duration-200 hover:bg-royal/5">
        <MapPin className="h-3.5 w-3.5" /> Fix this property's location
      </button>
    );
  }
  return (
    <div data-testid="fix-location-form" className="mt-3 space-y-3 rounded-lg border border-border bg-secondary p-3.5">
      <p className="text-xs text-muted-foreground">
        No map service could find this address. Enter a corrected one — or, if the place genuinely
        isn't on the map, its coordinates.
      </p>
      <div className="flex flex-wrap gap-2">
        <input data-testid="fix-location-address" value={address} onChange={(e) => setAddress(e.target.value)}
               placeholder="e.g. 57689 NC Highway 12, Hatteras, NC 27943"
               className="h-10 min-w-0 flex-1 rounded-lg border border-border bg-card px-3 text-sm" />
        <button type="button" data-testid="fix-location-save-address" disabled={busy || address.trim().length < 8}
                onClick={() => submit({ address: address.trim() })}
                className="h-10 rounded-lg bg-royal px-3.5 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? "Checking…" : "Use address"}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="fix-location-lat" value={lat} onChange={(e) => setLat(e.target.value)}
               placeholder="Latitude" className="h-10 w-28 rounded-lg border border-border bg-card px-3 text-sm" />
        <input data-testid="fix-location-lon" value={lon} onChange={(e) => setLon(e.target.value)}
               placeholder="Longitude" className="h-10 w-28 rounded-lg border border-border bg-card px-3 text-sm" />
        <button type="button" data-testid="fix-location-save-coords" disabled={busy || !lat || !lon}
                onClick={() => submit({ lat: Number(lat), lon: Number(lon) })}
                className="h-10 rounded-lg border border-royal/30 px-3.5 text-xs font-semibold text-royal disabled:opacity-50">
          Use coordinates
        </button>
        <button type="button" onClick={() => setOpen(false)}
                className="h-10 px-2 text-xs text-muted-foreground">Cancel</button>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, icon: Icon, testid }) {  return (
    <div data-testid={testid} className="min-w-0">
      <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground break-words">
        {Icon && <Icon className="h-3 w-3 shrink-0" />} {label}
      </p>
      <p className="mt-0.5 font-display text-xl font-bold text-royal-deep">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground break-words">{hint}</p>}
    </div>
  );
}

/** One-line banner for a work order: what the weather is doing at that job's property. */
export function WeatherLine({ propertyId }) {
  const [wx, setWx] = useState(null);
  useEffect(() => {
    let live = true;
    setWx(null);
    if (!propertyId) return undefined;
    api.get(`/weather/property/${propertyId}`)
      .then(({ data }) => live && data.property_id === propertyId && setWx(data))
      .catch(() => {});
    return () => { live = false; };
  }, [propertyId]);
  if (!wx || wx.status === "disabled") return null;
  const alerts = wx.alerts || [];
  const today = wx.weather?.today || {};
  const level = LEVELS[wx.severity || "normal"] || LEVELS.normal;
  const stale = wx.status !== "current";
  return (
    <div data-testid={`weather-line-${propertyId}`}
         className={`flex flex-wrap items-center gap-2 rounded-lg border px-3.5 py-2 text-xs ${level.cls}`}>
      <CloudSun className="h-3.5 w-3.5 shrink-0" />
      {alerts.length > 0 ? (
        <span className="font-semibold">{alerts.map((a) => a.event).join(" · ")} in effect at this property (NWS)</span>
      ) : wx.status === "unavailable" ? (
        <span>Official weather unavailable for this property</span>
      ) : (
        <span>
          {today.forecast}
          {today.temperature_f != null ? ` · ${today.temperature_f}°F` : ""}
          {today.precip_probability_pct ? ` · ${today.precip_probability_pct}% rain` : ""}
        </span>
      )}
      {stale && wx.status !== "unavailable" && <span className="opacity-80">(data {ago(wx.last_successful_update)})</span>}
    </div>
  );
}

export default WeatherPanel;
